/**
 * Theme preference store. Arc themes through `data-theme="dark"` on <html>; this keeps the
 * preference (light | dark | system) in localStorage and applies the resolved theme.
 */
import { useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const KEY = 'scenewise_theme';
const listeners = new Set<() => void>();
let preference: ThemePreference = read();

function read(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

function systemTheme(): ResolvedTheme {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveTheme(p: ThemePreference = preference): ResolvedTheme {
  return p === 'system' ? systemTheme() : p;
}

function apply() {
  const t = resolveTheme();
  const root = document.documentElement;
  if (t === 'dark') root.setAttribute('data-theme', 'dark');
  else root.removeAttribute('data-theme');
  root.style.colorScheme = t;
}

export function setThemePreference(p: ThemePreference) {
  preference = p;
  try {
    localStorage.setItem(KEY, p);
  } catch {
    /* ignore */
  }
  apply();
  listeners.forEach((l) => l());
}

export function getThemePreference(): ThemePreference {
  return preference;
}

/** Call once at startup: applies the stored preference and follows the OS when on `system`. */
export function initTheme() {
  apply();
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', () => {
    if (preference === 'system') {
      apply();
      listeners.forEach((l) => l());
    }
  });
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, getThemePreference, () => 'system');
}

export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribe, () => resolveTheme(), () => 'light');
}
